import { useEffect, useRef } from 'react';
import { useI18n, type CopyKey } from '../i18n';
import type { Suggestion } from './suggest';

/**
 * The box, and it is never anywhere but the bottom of the screen.
 *
 * Nothing here is positioned. It is the last row of the chat's flex column and the column
 * is exactly as tall as the visible viewport, so the composer sits above the keyboard
 * because there is nowhere else for it to be — see `viewport.ts` for why every attempt to
 * pin it with `position: fixed` goes wrong on iOS.
 *
 * The textarea grows with what is typed, to a ceiling. A fixed two-row box makes somebody
 * writing a paragraph scroll inside a two-line window; a box with no ceiling eats the
 * conversation they are answering. Four rows is roughly where a real answer sits and is
 * still less than a third of a phone screen.
 */

const WORDS: Record<Suggestion, CopyKey> = {
  dontKnow: 'chat.word.dontKnow',
  dispute: 'chat.word.dispute',
  more: 'chat.word.more',
  moreQuestions: 'chat.word.moreQuestions',
};

const CHIP_LABELS: Record<Suggestion, CopyKey> = {
  dontKnow: 'chat.chip.dontKnow',
  dispute: 'chat.chip.dispute',
  more: 'chat.chip.more',
  moreQuestions: 'chat.chip.moreQuestions',
};

/** The tallest the box grows before it starts scrolling inside itself. */
const MAX_ROWS = 4;

export default function Composer({
  draft,
  onDraft,
  onSend,
  sending,
  suggestions,
}: {
  draft: string;
  onDraft: (next: string) => void;
  onSend: () => void;
  sending: boolean;
  suggestions: Suggestion[];
}) {
  const { t } = useI18n();
  const box = useRef<HTMLTextAreaElement | null>(null);

  /*
   * Grown to fit, by measuring rather than by counting newlines.
   *
   * A count of `\n` is wrong the moment a line wraps, which on a phone is most lines of a
   * real answer — the box would stay one row tall while three rows of text scrolled inside
   * it. Resetting to `auto` before reading `scrollHeight` is what makes it shrink again
   * when text is deleted; without that reset the height only ever ratchets up, and a
   * learner who pastes a paragraph and then clears it is left with a box that has eaten
   * half the thread.
   */
  useEffect(() => {
    const element = box.current;
    if (!element) return;
    element.style.height = 'auto';
    const line = Number.parseFloat(getComputedStyle(element).lineHeight) || 24;
    const ceiling = line * MAX_ROWS + 24;
    element.style.height = `${Math.min(element.scrollHeight, ceiling)}px`;
  }, [draft]);

  const fill = (suggestion: Suggestion) => {
    onDraft(t(WORDS[suggestion]));
    box.current?.focus();
  };

  return (
    <form
      className="chat-composer"
      onSubmit={(event) => {
        event.preventDefault();
        onSend();
      }}
    >
      {suggestions.length > 0 && (
        <div className="chat-chips" role="group" aria-label={t('chat.chips.label')}>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              className="chat-chip"
              onClick={() => fill(suggestion)}
            >
              {t(CHIP_LABELS[suggestion])}
            </button>
          ))}
        </div>
      )}

      <div className="chat-composer-row">
        <label className="chat-sr" htmlFor="chat-draft">
          {t('chat.input.label')}
        </label>
        <textarea
          id="chat-draft"
          ref={box}
          className="chat-draft"
          value={draft}
          onChange={(event) => onDraft(event.target.value)}
          placeholder={t('chat.input.placeholder')}
          rows={1}
          /* Sentences, because an answer is one. iOS otherwise applies the last setting the
             page used, and a capital letter missing from the front of every answer reads as
             the app being careless rather than as a browser default. */
          autoCapitalize="sentences"
          /* Enter inserts a newline rather than sending, because a multiple choice is
             answered with a number and an explanation is answered with sentences, and a
             phone keyboard's return key is where people go for the second line.
             `enterkeyhint` says so on the key itself, so nobody has to find out by losing
             a half-written answer. */
          enterKeyHint="enter"
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              onSend();
            }
          }}
        />
        <button
          type="submit"
          className="chat-send"
          disabled={sending || draft.trim() === ''}
          aria-label={t('chat.send')}
        >
          {/* An arrow rather than the word, because the word is three times the width on a
              row that also has to hold a box worth typing in, and every chat a learner has
              ever used puts an arrow here. The word is still on the button for anything
              that reads it aloud. */}
          <span aria-hidden="true">{sending ? '…' : '↑'}</span>
        </button>
      </div>
    </form>
  );
}
